import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType = 'website',
  schema,
}: SEOProps) => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = `${siteUrl}${canonical}`;
    }

    // Open Graph tags
    if (ogTitle) {
      updateMetaTag('og:title', ogTitle, true);
    }

    if (ogDescription) {
      updateMetaTag('og:description', ogDescription, true);
    }

    if (ogType) {
      updateMetaTag('og:type', ogType, true);
    }

    if (canonical) {
      updateMetaTag('og:url', `${siteUrl}${canonical}`, true);
    }

    // Twitter Card tags
    if (ogTitle) {
      updateMetaTag('twitter:title', ogTitle);
    }

    if (ogDescription) {
      updateMetaTag('twitter:description', ogDescription);
    }

    // Schema.org JSON-LD
    if (schema) {
      let script = document.querySelector('script[type="application/ld+json"]#page-schema');
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('id', 'page-schema');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    }

    // Update last-modified
    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    }
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogType, schema]);
};

export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'LBAs Essentials',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: 'Consultoria especializada em melhoria de processos para MEI e pequenas empresas',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: 'Portuguese',
    },
  };
};

export const generateWebPageSchema = (name: string, description: string, url: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: `${siteUrl}${url}`,
    publisher: {
      '@type': 'Organization',
      name: 'LBAs Essentials',
    },
  };
};

export const generateServiceSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'LBAs Essentials - Programa de Melhoria de Processos',
    description: 'Programa de consultoria de 10-12 meses focado em produtividade, organização e eficiência operacional para MEI e pequenas empresas',
    provider: {
      '@type': 'Organization',
      name: 'LBAs Essentials',
      url: siteUrl,
    },
    areaServed: 'BR',
    serviceType: 'Consultoria Empresarial',
  };
};

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};
